import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";

import { toastLocalizedOperationError } from "./alerts";
import { rollbarAndLogError } from "./logger";
import { ModelType } from "./type-utils";

import { DraggableItemType } from "mds/components/DragAndDrop";

export const useLocalCopy = <T>(collection: T): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [localCollection, setLocalCollection] = useState<T>(collection);
  // Sync when the external collection changes, without an effect (avoids an extra render
  // and infinite loops from reference changes). Local writes via setLocalCollection remain.
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevCollection, setPrevCollection] = useState(collection);
  if (!isEqual(collection, prevCollection)) {
    setPrevCollection(collection);
    setLocalCollection(collection);
  }

  return [localCollection, setLocalCollection];
};

/**
 * Copy each item when the store list changes. Drag ordering writes `order` and
 * the category field onto the item, and the activity selectors sort and filter
 * on those same fields of the redux entities. Those entities are frozen, so the
 * write throws and the list never moves. Copies keep the write off the store
 * until drop persists it.
 */
export const useMutableListCopy = <T extends object>(items: readonly T[]): T[] =>
  useMemo(() => items.map((item) => ({ ...item })), [items]);

/**
 * Insert an item into a collection at a specific index by updating the order
 * of the item according to the surrounding items, and return the newly sorted collection.
 *
 * See OrderedModel on the backend for more details on our ordering system and choice of
 * algorithm and data types.
 */
export const reorderItemInCollection = <T extends DraggableItemType>(
  itemToMove: T,
  collection: T[],
  toIndex: number,
) => {
  // Create a new collection in the desired order. The incoming collection should already
  // be sorted in this order, but we sort it explicitly here just in case it isn't.
  const newCollection = collection.filter((item) => item.id !== itemToMove.id);
  newCollection.sort((a, b) => a.order - b.order);
  newCollection.splice(toIndex, 0, itemToMove);

  // Check for valid arguments.
  if (toIndex < 0 || toIndex >= newCollection.length) {
    rollbarAndLogError(`Invalid index (${toIndex}) for collection size ${newCollection.length}`);
    return collection;
  }

  // Determine the new order to assign to the item being moved.
  let newOrder: number;
  if (newCollection.length === 1) {
    newOrder = 0;
  } else if (toIndex === 0) {
    newOrder = newCollection[1].order - 1;
  } else if (toIndex === newCollection.length - 1) {
    newOrder = newCollection[newCollection.length - 2].order + 1;
  } else {
    // Compute a new order for the item by finding the midpoint between its new neighbors.
    const beforeOrder = newCollection[toIndex - 1].order;
    const afterOrder = newCollection[toIndex + 1].order;
    newOrder = (beforeOrder + afterOrder) / 2;

    // If the limit of floating-point precision has been reached such that we can no longer
    // find a value that lies strictly between `beforeOrder` and `afterOrder`, we report an
    // error and return the original collection unmodified.
    const hasCollision = newOrder === beforeOrder || newOrder === afterOrder;
    if (hasCollision) {
      const error = new Error("New order is the same as the adjacent item. Float precision limit?");
      rollbarAndLogError(error);
      toastLocalizedOperationError("failed_to_reorder_item");
      return collection;
    }
  }

  // eslint-disable-next-line no-param-reassign
  itemToMove.order = newOrder;

  return newCollection;
};

/**
 * This function is used to change the order of an item in a list of draggable items.
 * It's specific to our Drag and Drop implementation and may need some refactoring
 * to be useful it that code changes
 *
 * @param typeAttribute If we move an item between categories, we need to know which
 * attribute identifies the item as belonging to a category, so we can update it.
 */
export const useDnDItemOrdering = <T extends DraggableItemType>(
  itemCollection: T[],
  setFunction: (newCollection: T[]) => void,
  typeAttribute?: string,
) => {
  return useCallback(
    (item: T, toIndex: number, dropContainerId: string | number) => {
      const itemIndex = itemCollection.findIndex((ci) => ci.id === item.id);
      if (itemIndex === toIndex) {
        return;
      }

      if (typeAttribute) {
        // eslint-disable-next-line no-param-reassign
        item[typeAttribute] = dropContainerId;
      }

      const newCollection = reorderItemInCollection(item, itemCollection, toIndex);
      setFunction(newCollection);
    },
    [itemCollection, setFunction, typeAttribute],
  );
};

export const sortById = (a: ModelType, b: ModelType) => (a.id > b.id ? 1 : -1);

/** Filter then map in one pass. */
export const filterMap = <T, U>(
  items: readonly T[],
  predicate: (item: T, index: number) => boolean,
  mapper: (item: T, index: number) => U,
): U[] => {
  const result: U[] = [];
  items.forEach((item, index) => {
    if (predicate(item, index)) {
      result.push(mapper(item, index));
    }
  });
  return result;
};

/** Map then drop `null` / `undefined` results in one pass. */
export const compactMap = <T, U>(
  items: readonly T[],
  mapper: (item: T, index: number) => U | null | undefined,
): U[] => {
  const result: U[] = [];
  items.forEach((item, index) => {
    const mapped = mapper(item, index);
    if (mapped != null) {
      result.push(mapped);
    }
  });
  return result;
};
